import type { Candle } from '../../types/api';
import { apiUrl } from '../../config/runtime';
import { cachedRequest } from './cache';
import { ApiError } from './client';

export interface CandleQuery {
  symbol: string;
  interval: string;
  from?: number;
  to?: number;
  limit?: number;
}

export interface CandleHydrationMeta {
  dataStatus?: string;
  hydrationStatus?: string;
  hydrationPrefetch?: string;
  jobId?: string;
  hotOldest?: number;
  hotLatest?: number;
  retryAfterMs?: number;
}

export interface CandleResult {
  candles: Candle[];
  hydration: CandleHydrationMeta;
}

export function fetchCandles(query: CandleQuery): Promise<Candle[]> {
	return fetchCandlesWithMeta(query).then((result) => result.candles);
}

export function fetchCandlesWithMeta(query: CandleQuery): Promise<CandleResult> {
  const params = new URLSearchParams({
    symbol: query.symbol,
    interval: query.interval,
  });

  if (query.from !== undefined) params.set('from', String(query.from));
  if (query.to !== undefined) params.set('to', String(query.to));
  if (query.limit !== undefined) params.set('limit', String(query.limit));

  const path = `/api/candles?${params.toString()}` as `/api/${string}`;
  return cachedRequest(
    `candles:${params.toString()}`,
    (result) => candleCacheTTL(query, result.hydration),
    () => fetchCandlesResponse(path),
  );
}

async function fetchCandlesResponse(path: `/api/${string}`): Promise<CandleResult> {
  const response = await fetch(apiUrl(path), { credentials: 'same-origin' });
  if (!response.ok) {
    throw new ApiError(response.status, `HTTP ${response.status}`);
  }
  const candles = await response.json() as Candle[];
  return { candles, hydration: candleHydrationMeta(response.headers) };
}

function candleHydrationMeta(headers: Headers): CandleHydrationMeta {
  const retryAfter = Number.parseInt(headers.get('Retry-After') || '', 10);
  return {
    dataStatus: headerValue(headers, 'X-Candle-Data-Status'),
    hydrationStatus: headerValue(headers, 'X-Candle-Hydration-Status'),
    hydrationPrefetch: headerValue(headers, 'X-Candle-Hydration-Prefetch'),
    jobId: headerValue(headers, 'X-Candle-Hydration-Job-ID'),
    hotOldest: headerNumber(headers, 'X-Candle-Hot-Oldest'),
    hotLatest: headerNumber(headers, 'X-Candle-Hot-Latest'),
    retryAfterMs: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : undefined,
  };
}

function headerValue(headers: Headers, name: string): string | undefined {
  return headers.get(name) || undefined;
}

function headerNumber(headers: Headers, name: string): number | undefined {
  const value = Number.parseInt(headers.get(name) || '', 10);
  return Number.isFinite(value) ? value : undefined;
}

function candleCacheTTL(query: CandleQuery, hydration?: CandleHydrationMeta): number {
  if (hydration?.hydrationStatus === 'pending' || hydration?.hydrationStatus === 'running' || hydration?.dataStatus === 'archive-miss' || hydration?.dataStatus === 'partial-hot') {
    return Math.max(1_000, Math.min(hydration.retryAfterMs || 5_000, 10_000));
  }
  const now = Math.floor(Date.now() / 1000);
  if (query.from !== undefined && query.to !== undefined && query.to < now - 600) {
    return 5 * 60_000;
  }
  if (query.to === undefined || query.to >= now - 600) {
    return 5_000;
  }
  switch (query.interval) {
    case '1m':
    case '5m':
    case '15m':
    case '30m':
      return 15_000;
    case '1h':
    case '4h':
      return 60_000;
    case '1d':
    case '1wk':
    case '9wk':
      return 5 * 60_000;
    default:
      return 30_000;
  }
}
